import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
} from "react";
import CloseCircleIcon from "@/assets/icons/common/CloseCircleIcon";
import EyeIcon from "@/assets/icons/common/EyeIcon";
import EyeOffIcon from "@/assets/icons/common/EyeOffIcon";
import { useTapInteraction } from "@/shared/ui/pressable";
import InputShell from "./InputShell";
import styles from "./Input.module.scss";

type NativeInputAttrs = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value" | "size"
>;

type InputProps = NativeInputAttrs & {
  label: string;
  value?: string;
  onChange?: (value: string) => void;
  /**
   * 입력 중 숫자만 허용. type="number" 도 동일하게 동작한다.
   * 실제 input element 의 type 은 "text" 로 렌더링되어 iOS 의 numeric pad 와 일관된 동작을 유지한다.
   */
  numeric?: boolean;
  /** blur 시 양끝 공백을 trim 후 onChange 로 통보. password 는 항상 trim 하지 않음. */
  trimOnBlur?: boolean;
  /** focus + filled 상태에서 X 버튼 노출. */
  clearable?: boolean;
  /**
   * type="password" 시 비밀번호 표시/숨김 토글 버튼 노출.
   * filled 상태(value.length > 0)에서 눈 아이콘이 나타나며,
   * 클릭 시 실제 input type이 "text" ↔ "password"로 전환된다.
   */
  revealable?: boolean;
  /**
   * truthy면 InputShell 의 빨간 border(`shellError`)만 활성화. 메시지 텍스트는 렌더하지 않는다.
   * (디자인 정책: 에러 메시지는 input 하단에 노출하지 않음)
   */
  error?: string;
  /**
   * 지정 시 shell 클릭이 내부 input 포커스 대신 이 핸들러를 호출한다.
   * 확인 화면의 읽기전용 필드를 "탭하면 편집 화면으로 복귀"하는 버튼처럼 쓰기 위한 용도.
   * 미지정 시 기존 동작(내부 input 포커스) 그대로.
   */
  onFieldClick?: () => void;
};

const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    value = "",
    onChange,
    numeric,
    trimOnBlur,
    clearable,
    revealable,
    error,
    type,
    placeholder,
    disabled,
    className,
    onFocus,
    onBlur,
    inputMode,
    pattern,
    onFieldClick,
    ...rest
  },
  ref,
) {
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const [focused, setFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const filled = value.length > 0;
  const { isPressed: revealPressed, ref: revealRef, pressHandlers: revealHandlers } = useTapInteraction<HTMLButtonElement>();
  const { isPressed: clearPressed, ref: clearRef, pressHandlers: clearHandlers } = useTapInteraction<HTMLButtonElement>();
  const isNumeric = numeric || type === "number";
  const isPasswordType = type === "password";

  // revealable(눈 아이콘)이 꺼지면 이전에 켠 "보기"를 리셋한다. combined 비밀번호 흐름은
  // 입력→확인 step 전환 시 같은 Input 인스턴스가 살아남으므로(같은 screenKey), 확인 step 에서
  // revealable=false 가 되어도 stale showPassword 가 남는다. → 되돌아왔을 때 마스킹으로 시작하게 청소.
  useEffect(() => {
    if (!revealable) setShowPassword(false);
  }, [revealable]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!onChange) return;
    const next = isNumeric
      ? e.target.value.replace(/[^0-9]/g, "")
      : e.target.value;
    onChange(next);
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    if (trimOnBlur && type !== "password" && onChange) {
      const trimmed = value.trim();
      if (trimmed !== value) onChange(trimmed);
    }
    onBlur?.(e);
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  const handleClear = () => {
    onChange?.("");
    focusInput();
  };

  // type 결정: numeric → "text", revealable 하면서 공개 중인 비밀번호 → "text", 그 외 → 원본 type.
  // revealable 게이트: 눈 아이콘이 없는(revealable=false) 필드는 stale showPassword 여도 절대 평문으로
  // 렌더하지 않는다(동기 차단이라 step 전환 프레임의 평문 플래시도 방지).
  const resolvedType = isNumeric
    ? "text"
    : isPasswordType && revealable && showPassword
      ? "text"
      : type;

  const handleToggleReveal = () => {
    setShowPassword((prev) => !prev);
    // input type 이 password ↔ text 로 바뀌면 브라우저가 selection 을 0 으로 reset 한다.
    // 다음 frame 에 focus + setSelectionRange(끝, 끝) 로 caret 을 값 끝으로 복원.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const end = el.value.length;
      try {
        el.setSelectionRange(end, end);
      } catch {
        // 일부 브라우저에서 password type 의 setSelectionRange 가 throw 할 수 있어 무시
      }
    });
  };

  return (
    <InputShell
      label={label}
      focused={focused}
      filled={filled}
      disabled={disabled}
      error={error}
      className={className}
      onClick={onFieldClick ?? focusInput}
    >
      <input
        {...rest}
        ref={inputRef}
        className={styles.input}
        type={resolvedType}
        inputMode={isNumeric ? "numeric" : inputMode}
        pattern={isNumeric ? "[0-9]*" : pattern}
        placeholder={focused ? placeholder : ""}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
      />
      {/* Figma 217:8389 — Visibility 토글 먼저, Circle Close(clear) 다음 (gap 8px) */}
      {revealable && isPasswordType && filled && focused && !disabled && (
        <button
          ref={revealRef}
          type="button"
          className={`${styles.revealBtn} ${revealPressed ? styles.pressed : ""}`.trim()}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleToggleReveal}
          aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 표시"}
          aria-pressed={showPassword}
          {...revealHandlers}
        >
          {showPassword ? (
            <EyeOffIcon width="24" height="24" />
          ) : (
            <EyeIcon width="24" height="24" />
          )}
        </button>
      )}
      {clearable && filled && focused && !disabled && (
        <button
          ref={clearRef}
          type="button"
          className={`${styles.clearBtn} ${clearPressed ? styles.pressed : ""}`.trim()}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClear}
          aria-label={`${label} 입력값 지우기`}
          {...clearHandlers}
        >
          <CloseCircleIcon width="24" height="24" />
        </button>
      )}
    </InputShell>
  );
});

export default Input;
export type { InputProps };
